import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Sparkles, Eye, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * "Za tebe" — personalised article widget driven by the interests the
 * user picked during onboarding (`/api/public/me/interests`). Renders
 * only when:
 *   1) The admin toggle is ON (homepage_settings.personalized_feed.enabled).
 *   2) The user is authenticated.
 *   3) The user has at least one saved interest *and* the backend
 *      returned at least one matching article.
 *
 * Silently no-ops in every other case — no empty boxes for visitors.
 */
export default function ForYouWidget() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [config, setConfig] = useState(null);     // null = still checking
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 1 — public gate (also gives us configured title + limit)
  useEffect(() => {
    fetch(`${API}/public/foryou/status`)
      .then(r => r.json())
      .then(d => setConfig(d || { enabled: false }))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  // Step 2 — fetch personalised feed once user is logged in & widget enabled
  useEffect(() => {
    if (!config?.enabled || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const r = await fetch(`${API}/public/foryou?limit=${config.limit || 6}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) setData(await r.json());
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [config, isAuthenticated, getAccessTokenSilently]);

  if (!config?.enabled || !isAuthenticated) return null;
  if (loading || !data) return null;
  const articles = data.articles || [];
  if (articles.length === 0) return null;

  return (
    <section
      data-testid="foryou-widget"
      className="rounded-2xl border border-red-100 dark:border-red-900/40 bg-gradient-to-br from-red-50/80 to-white dark:from-red-950/20 dark:to-[#0f172a] p-5 sm:p-6 my-8"
    >
      <header className="flex items-center justify-between mb-5">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
          <Sparkles size={16} className="text-red-500" />
          {config.title_hr || 'Za tebe'}
        </h2>
        {(data.interests || []).length > 0 && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 hidden sm:block">
            Na temelju: {data.interests.slice(0, 4).join(' · ')}
            {data.interests.length > 4 ? ' …' : ''}
          </p>
        )}
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {articles.map((a, i) => {
          const cover = a.cover_image_url?.startsWith('http')
            ? a.cover_image_url
            : (a.cover_image_url ? `${BACKEND}${a.cover_image_url}` : null);
          return (
            <Link
              key={a.id}
              to={`/clanak/${a.slug}`}
              className="group block bg-white dark:bg-[#0b1220] rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:border-red-300 dark:hover:border-red-700 transition-colors"
              data-testid={`foryou-article-${i}`}
            >
              {cover && (
                <div className="aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-gray-900">
                  <img src={cover} alt="" loading="lazy"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-3">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-red-600 leading-snug">
                  {a.title_hr}
                </h3>
                {a.excerpt_hr && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                    {a.excerpt_hr}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                  {a.publish_at && (
                    <span className="flex items-center gap-1">
                      <Calendar size={10} />
                      {new Date(a.publish_at).toLocaleDateString('hr-HR')}
                    </span>
                  )}
                  <span className="flex items-center gap-1"><Eye size={10} />{a.views || 0}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
